// frontend/src/services/eventosService.js
import axios from 'axios';

// Los endpoints de eventos/actividades/tareas/timelogs están bajo /api/eventos/
const API_URL = process.env.REACT_APP_API_URL ?
  process.env.REACT_APP_API_URL.replace('/api/content', '/api/eventos') :
  'http://localhost:8000/api/eventos';

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Obtener CSRF token
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// Añadir CSRF token a peticiones POST/PUT/DELETE
api.interceptors.request.use((config) => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    config.headers['X-CSRFToken'] = getCookie('csrftoken');
  }
  return config;
});

// Interceptor para errores
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      console.log('No autenticado - redirigir a login');
    }
    return Promise.reject(error);
  }
);

const eventosService = {
  // ============================================
  // EVENTOS - CRUD
  // ============================================

  /**
   * Obtiene listado de eventos con filtros opcionales
   * @param {Object} params - Parámetros de filtrado (status, tipo_evento, cliente, proyecto, etc.)
   * @returns {Promise}
   */
  getEventos: (params) => api.get('eventos/', { params }),

  /**
   * Obtiene detalle de un evento específico
   * @param {number} id - ID del evento
   * @returns {Promise}
   */
  getEvento: (id) => api.get(`eventos/${id}/`),

  /**
   * Crea un nuevo evento
   * @param {Object} data - Datos del evento
   * @returns {Promise}
   */
  createEvento: (data) => api.post('eventos/', data),

  /**
   * Actualiza un evento existente
   * @param {number} id - ID del evento
   * @param {Object} data - Datos a actualizar
   * @returns {Promise}
   */
  updateEvento: (id, data) => api.patch(`eventos/${id}/`, data),

  /**
   * Elimina un evento
   * @param {number} id - ID del evento
   * @returns {Promise}
   */
  deleteEvento: (id) => api.delete(`eventos/${id}/`),

  // ============================================
  // EVENTOS - ENDPOINTS ESPECIALES
  // ============================================

  /**
   * Obtiene eventos optimizados para calendario
   * @param {Object} params - Parámetros de filtrado (fecha_inicio, fecha_fin, etc.)
   * @returns {Promise}
   */
  getCalendar: (params) => api.get('eventos/calendar/', { params }),

  /**
   * Cambia el estado de un evento
   * @param {number} eventoId - ID del evento
   * @param {string} status - Nuevo estado ('draft', 'planning', 'confirmed', 'in_progress', 'completed', 'cancelled', 'postponed')
   * @returns {Promise}
   */
  changeStatus: (eventoId, status) =>
    api.post(`eventos/${eventoId}/change_status/`, { status }),

  // ============================================
  // HELPERS
  // ============================================

  /**
   * Obtiene eventos para un rango de fechas (útil para calendarios)
   * @param {string} startDate - Fecha inicio (YYYY-MM-DD)
   * @param {string} endDate - Fecha fin (YYYY-MM-DD)
   * @returns {Promise}
   */
  getEventosByDateRange: (startDate, endDate) =>
    api.get('eventos/calendar/', {
      params: {
        fecha_inicio: startDate,
        fecha_fin: endDate
      }
    }),

  /**
   * Obtiene eventos por estado
   * @param {string} status - Estado del evento
   * @returns {Promise}
   */
  getEventosByStatus: (status) =>
    api.get('eventos/', { params: { status } }),

  /**
   * Obtiene eventos por cliente
   * @param {number} clienteId - ID del cliente
   * @returns {Promise}
   */
  getEventosByCliente: (clienteId) =>
    api.get('eventos/', { params: { cliente: clienteId } }),

  /**
   * Obtiene eventos por proyecto
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise}
   */
  getEventosByProyecto: (proyectoId) =>
    api.get('eventos/', { params: { proyecto: proyectoId } }),

  /**
   * Obtiene eventos por tipo
   * @param {string} tipoEvento - Tipo de evento ('lanzamiento', 'corporativo', 'social', etc.)
   * @returns {Promise}
   */
  getEventosByTipo: (tipoEvento) =>
    api.get('eventos/', { params: { tipo_evento: tipoEvento } }),

  // ============================================
  // TAREAS DE EVENTO
  // ============================================

  /**
   * Elimina una tarea de evento
   * @param {number} tareaId - ID de la tarea evento
   * @returns {Promise}
   */
  deleteTareaEvento: (tareaId) => api.delete(`tareas/${tareaId}/`),
};

export default eventosService;
